import React from 'react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

interface FAQProps {
  cityName?: string;
}

export const FAQ: React.FC<FAQProps> = ({ cityName = "Long Island" }) => {
  const faqs = [
    {
      question: `How much does roof replacement cost in ${cityName}?`,
      answer: `Roof replacement costs in ${cityName} typically range from $8,000 to $25,000 depending on the size of your home, materials chosen, and complexity of the job. We provide free detailed estimates for all roofing projects. Factors affecting cost include roof size, material type (asphalt shingles, metal, tile), roof pitch, and any structural repairs needed.`
    },
    {
      question: "How long does a roof replacement take?",
      answer: "Most residential roof replacements take 1-3 days to complete, depending on the size and complexity of your roof. Weather conditions and material availability can affect timing. We work efficiently to minimize disruption to your daily routine while ensuring quality workmanship."
    },
    {
      question: "Do you handle insurance claims for storm damage?",
      answer: "Yes, we work directly with insurance companies to help process storm damage claims. We'll inspect your roof, document damage, and provide detailed estimates for insurance adjusters. Our team has extensive experience with insurance claims and will advocate for you throughout the process."
    },
    {
      question: "What types of roofing materials do you install?",
      answer: "We install all major roofing materials including asphalt shingles (GAF, Owens Corning, CertainTeed), metal roofing, slate, tile, and flat roof systems. As a GAF Master Elite contractor, we offer extended warranties on GAF products. We'll help you choose the best material for your budget, climate, and aesthetic preferences."
    },
    {
      question: "Do you offer emergency roof repair services?",
      answer: "Yes, we provide 24/7 emergency roof repair services throughout Long Island. If you have a roof leak, storm damage, or other urgent roofing issue, call us immediately. We'll provide temporary protection and permanent repair solutions to protect your home."
    },
    {
      question: "Are you licensed and insured?",
      answer: "Yes, Safeway Roofing & Chimney is fully licensed and insured in New York State. We carry comprehensive liability insurance and workers' compensation coverage. We're also a GAF Master Elite certified contractor, which means we meet strict criteria for licensing, insurance, and customer satisfaction."
    },
    {
      question: "What warranty do you provide on roofing work?",
      answer: "We provide comprehensive warranties on all our roofing work. Our workmanship warranty covers installation for up to 10 years, and manufacturer warranties on materials range from 20 years to lifetime depending on the product. As a GAF Master Elite contractor, we can offer extended System Plus warranties up to 50 years."
    },
    {
      question: `What areas in ${cityName} do you serve?`,
      answer: `We serve all of ${cityName} including Nassau County and Suffolk County. Our service areas include Hempstead, Babylon, Huntington, Smithtown, Brookhaven, Islip, Oyster Bay, and all surrounding communities. We're centrally located to provide fast, reliable service throughout Long Island.`
    }
  ];

  // Structured data for FAQ
  const faqStructuredData = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  // Add structured data to head
  React.useEffect(() => {
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(faqStructuredData);
    script.id = 'faq-structured-data';
    document.head.appendChild(script);

    return () => {
      const existingScript = document.getElementById('faq-structured-data');
      if (existingScript) {
        document.head.removeChild(existingScript);
      }
    };
  }, [cityName]);

  return (
    <section id="faq" className="py-16 bg-background">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
            Frequently Asked Questions
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Get answers to common questions about roofing services in {cityName}
          </p>
        </div>
        
        <div className="max-w-4xl mx-auto">
          <Accordion type="single" collapsible className="space-y-4">
            {faqs.map((faq, index) => (
              <AccordionItem key={index} value={`item-${index}`} className="bg-card rounded-lg border border-border">
                <AccordionTrigger className="px-6 py-4 text-left font-semibold text-card-foreground hover:no-underline hover:bg-secondary/50 rounded-t-lg">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="px-6 pb-4 text-muted-foreground leading-relaxed">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </div>
    </section>
  );
};